import Phaser from "phaser";
import type { AssetManifest } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";
import type { LiveProp } from "./prop-occlusion.ts";

/**
 * The images of removed props a prop view keeps, hidden and as a new image is, for the props it adds next, so a view
 * that scrolls props in and out does not create and destroy an image for each.
 */
export class PropImagePool {
  private readonly spare: Phaser.GameObjects.Image[] = [];

  /**
   * @param scene The scene the images belong to.
   * @param manifest The asset manifest, for a page's texture.
   * @param size The most images kept; 0 destroys every removed prop's image.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly manifest: AssetManifest,
    private readonly size: number,
  ) {}

  /**
   * Number of hidden images kept.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.spare.length;
  }

  /**
   * An image for a new prop: a kept one, or a new one on the page's texture, its origin at its bottom left.
   *
   * @param page The atlas page.
   * @param frame The frame.
   * @returns The image.
   */
  public take(page: number, frame: string): Phaser.GameObjects.Image {
    return this.spare.pop() ?? this.scene.add.image(0, 0, pageTextureKey(this.manifest, page), frame).setOrigin(0, 1);
  }

  /**
   * Gives a prop's image back: hidden and as a new image is (unflipped, untinted, at full alpha), or destroyed when the
   * pool is full. The entry says what the image was drawn with.
   *
   * @param entry The prop it belonged to, as it was drawn.
   */
  public release(entry: LiveProp): void {
    const image = entry.image;
    if (this.spare.length >= this.size) {
      image.destroy();
      return;
    }
    image.setVisible(false);
    if (entry.flipX || entry.flipY) {
      image.setFlip(false, false);
    }
    if (entry.selected) {
      image.clearTint();
    }
    if (image.alpha !== 1) {
      image.setAlpha(1);
    }
    this.spare.push(image);
  }

  /**
   * Destroys every kept image.
   */
  public clear(): void {
    for (const image of this.spare) {
      image.destroy();
    }
    this.spare.length = 0;
  }
}
